import React, { memo, useEffect, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import { EightK, Info } from "@mui/icons-material";
import { Button, Tooltip, Typography } from "@mui/material";
import useBreakpoints from "../../Utils/Hooks/useBreakPoint";
import { Done, Clear } from "@mui/icons-material";

interface stringMaps {
    [key: string]: string;
}

interface numberMaps {
    [key: string]: number;
}

interface rows {
    id: number;
    secsLeftToComplete: number;
    name: string;
    buttons: number;
    duration: number;
    information: string;
    typeUUID: string;
    completed: boolean;
    dateTimeDue: string;
    dateTimeStarted: string;
    AnswersUuid: string;
    portalUuid: string;
    DisplayOrder?: number;
    formattedDisplayOrder?: number;
    push: any;
}

function GetColumns(
    questionnaireTypeUUIDs: stringMaps,
    questionnaireDurations: numberMaps,
    tempTimeLeftMap: numberMaps,
    patientsUuid: string,
    answersUuid: stringMaps,
    push: any,
    portalUuid: string
): GridColDef[] {
    const breakPoint = useBreakpoints();

    function convertSecondsToTime(secs: number): string {
        let days = Math.floor(secs / (24 * 60 * 60));
        let hours = Math.floor((secs - days * 24 * 60 * 60) / (60 * 60));
        let minutes = Math.floor((secs - days * 24 * 60 * 60 - hours * 60 * 60) / 60);

        let dayString = days > 0 ? days + "d " : "";
        let hourString = hours > 0 ? hours + "h " : "";
        let minuteString = minutes > 0 ? minutes + "m " : "";

        return dayString + hourString + minuteString;
    }

    let nameColObj = {
        field: "name",
        headerName: "Questionnaire Name",
        width: 220,
        sortable: false,
    };
    let secsLeftToCompleteColObj = {
        field: "secsLeftToComplete",
        headerName: "Time left",
        width: 120,
        sortable: true,
        renderCell: (params: any) => {
            let answersUuid = params.getValue(params.id, "AnswersUuid");
            let typeUUID = questionnaireTypeUUIDs[answersUuid];
            if (tempTimeLeftMap[answersUuid] < 0 || tempTimeLeftMap[answersUuid] == null) {
                return;
            }
            return convertSecondsToTime(tempTimeLeftMap[answersUuid]);
        },
    };

    let buttonsWidth = breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? 130 : 75;
    let buttonsColObj = {
        field: "buttons",
        headerName: "",
        width: buttonsWidth,
        sortable: true,
        renderCell: (params: any) => {
            let answersUuid = params.getValue(params.id, "AnswersUuid");
            let typeUUID = questionnaireTypeUUIDs[answersUuid];
            let a = answersUuid;
            return (
                <>
                    {typeUUID.includes("completed-") &&
                    (tempTimeLeftMap[answersUuid] < 0 || tempTimeLeftMap[answersUuid] == null) ? (
                        <Button
                            sx={{
                                bgcolor: "#27ae60",
                                "&.MuiButtonBase-root:hover": {
                                    bgcolor: "#27ae60",
                                },
                            }}
                            variant="contained"
                            size={breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? "large" : "small"}
                            aria-label="questionnaire completed button"
                        >
                            Completed
                        </Button>
                    ) : typeUUID.includes("completed-") ? (
                        <Button
                            sx={{
                                bgcolor: "#27ae60",
                                "&.MuiButtonBase-root:hover": {
                                    bgcolor: "#55d64b",
                                },
                            }}
                            variant="contained"
                            size={breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? "large" : "small"}
                            aria-label="restart questionnaire button"
                            onClick={() =>
                                push(
                                    `/Questionnaire/Questionnaires?UUID=${patientsUuid}&questionnaire=${
                                        typeUUID.split("-")[1]
                                    }&a=${a}&u=${portalUuid}`
                                )
                            }
                        >
                            Restart
                        </Button>
                    ) : tempTimeLeftMap[answersUuid] < 0 || tempTimeLeftMap[answersUuid] == null ? (
                        <Button
                            sx={{
                                bgcolor: "#c0392b",
                                "&.MuiButtonBase-root:hover": {
                                    bgcolor: "#c0392b",
                                    boxShadow:
                                        "0px 3px 1px -2px rgb(0 0 0 / 20%), 0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%)",
                                },
                            }}
                            variant="contained"
                            size={breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? "large" : "small"}
                            aria-label="Questionnaire expired button"
                        >
                            Expired
                        </Button>
                    ) : (
                        <Button
                            sx={{
                                bgcolor: "#4BB543",
                                "&.MuiButtonBase-root:hover": {
                                    bgcolor: "#55d64b",
                                },
                            }}
                            variant="contained"
                            size={breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? "large" : "small"}
                            aria-label="start questionnaire button"
                            onClick={() =>
                                push(
                                    `/Questionnaire/Questionnaires?UUID=${patientsUuid}&questionnaire=${typeUUID}&a=${a}&u=${portalUuid}`
                                )
                            }
                        >
                            Start
                        </Button>
                    )}
                </>
            );
        },
    };
    let informationColObj = {
        field: "information",
        headerName: "Information",
        width: 130,
        sortable: false,
        renderCell: (params: any) => {
            let answersUuid = params.getValue(params.id, "AnswersUuid");

            return (
                <>
                    <Tooltip title={<Typography fontSize={20}>{params.getValue(params.id, "information")}</Typography>}>
                        <IconButton sx={{ color: "#1E92F4" }} aria-label="info about questionnaire">
                            <Info />
                        </IconButton>
                    </Tooltip>

                    {!(tempTimeLeftMap[answersUuid] < 0 || tempTimeLeftMap[answersUuid] == null) &&
                        questionnaireDurations[answersUuid] + " mins"}
                </>
            );
        },
    };

    if (breakPoint == "lg" || breakPoint == "md") {
        return [nameColObj, secsLeftToCompleteColObj, buttonsColObj, informationColObj];
    } else if (breakPoint == "smd") {
        return [nameColObj, buttonsColObj, informationColObj];
    } else {
        return [nameColObj, buttonsColObj];
    }
}

export const SingleSelectDataGrid = memo((props: any) => {
    const [timeLeft, setTimeLeftMap] = useState({});
    const [typeUUIDs, setTypeUUIDs] = useState({});
    const [durations, setDurations] = useState({});
    const [answersUuids, setAnswersUuids] = useState({});
    const [rows, setRows] = useState(props["rows"]);

    //update time left every 30 seconds, (as time does not need to be exact)
    useEffect(() => {
        setRows(props["rows"]);

        let tempTimeLeft = JSON.parse(JSON.stringify(timeLeft));
        let tempTypeUUIDs = JSON.parse(JSON.stringify(typeUUIDs));
        let tempDurations = JSON.parse(JSON.stringify(durations));
        let tempAnswersUuids = JSON.parse(JSON.stringify(answersUuids));

        let orderedRows: any[] = [];

        props["rows"].forEach((row: rows, i: number) => {
            //uncompleted 0-99
            //able to retry 100-199
            //completed 200-299
            //expired 300-399

            let displayOrder = row["DisplayOrder"] ? row["DisplayOrder"] : 99;
            let isCompleted = row["completed"];
            let isExpired = new Date(row["dateTimeDue"]) < new Date();

            let orderValue = displayOrder;

            //able to retry
            if (isCompleted && !isExpired) {
                orderValue += 100;
            }
            //completed
            else if (isCompleted && isExpired) {
                orderValue += 200;
            }
            //expired
            else if (!isCompleted && isExpired) {
                orderValue += 300;
            }
            row.formattedDisplayOrder = orderValue;

            orderedRows.push(row);
        });

        orderedRows.sort(function (a, b) {
            var valA = a.formattedDisplayOrder,
                valB = b.formattedDisplayOrder;
            // Compare the 2 dates
            if (valA < valB) return -1;
            if (valA > valB) return 1;
            return 0;
        });

        orderedRows.forEach((row: rows, i: number) => {
            let answersUuid = row["AnswersUuid"];
            let typeUUID = row["typeUUID"];
            if (row["completed"]) {
                typeUUID = "completed-" + typeUUID;
            }

            tempTypeUUIDs[answersUuid] = typeUUID;
            tempAnswersUuids[answersUuid] = answersUuid;
            tempDurations[answersUuid] = row["duration"];

            let secsLeftToComplete = new Date(row["dateTimeDue"]).getTime() / 1000 - new Date().getTime() / 1000;
            tempTimeLeft[answersUuid] = secsLeftToComplete;

            if (row["completed"]) {
                row["buttons"] = 99999999999999;
            } else if (secsLeftToComplete < 0) {
                row["buttons"] = 999999999999;
            } else {
                row["buttons"] = secsLeftToComplete;
            }
        });

        setTimeLeftMap(tempTimeLeft);
        setTypeUUIDs(tempTypeUUIDs);
        setAnswersUuids(tempAnswersUuids);
        setDurations(tempDurations);

        const timer = window.setInterval(() => {
            let tempTimeLeft = JSON.parse(JSON.stringify(timeLeft));

            rows.forEach((row: rows) => {
                let answersUuid = row["AnswersUuid"];
                let secsLeftToComplete = new Date(row["dateTimeDue"]).getTime() / 1000 - new Date().getTime() / 1000;

                if (secsLeftToComplete > 0) {
                    tempTimeLeft[answersUuid] = secsLeftToComplete;
                }
            });

            setTimeLeftMap(tempTimeLeft);
        }, 30000);
        return () => {
            window.clearInterval(timer);
        };
    }, [props["rows"]]);

    return (
        <div style={{ height: 400, width: "100%" }}>
            {props["rows"].length > 0 && (
                <DataGrid
                    rows={props["rows"]}
                    sortModel={[{ field: "buttons", sort: "asc" }]}
                    columns={GetColumns(
                        typeUUIDs,
                        durations,
                        timeLeft,
                        props["rows"][0]["patientsUUID"],
                        answersUuids,
                        props.push,
                        props["rows"][0]["portalUuid"]
                    )}
                    pageSize={5}
                    hideFooterSelectedRowCount
                />
            )}
        </div>
    );
});
